import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "../components/context/AuthProvider";
import { apiFetch } from "../services/apiClient";

interface WalkthroughContextType {
  isTourRunning: boolean;
  startTour: () => void;
  stopTour: () => void;
}

const WalkthroughContext = createContext<WalkthroughContextType | null>(null);

export const WalkthroughProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [isTourRunning, setIsTourRunning] = useState(false);

  // Auto-start on first login by checking the server-side flag
  useEffect(() => {
    if (!auth.isAuthenticated) return;

    apiFetch("/api/protected/user/profile")
      .then((res) => res.json())
      .then((profile: { has_seen_walkthrough: boolean }) => {
        if (!profile.has_seen_walkthrough) {
          setIsTourRunning(true);
        }
      })
      .catch(() => {
        // If the request fails, don't start the tour — avoid noise on auth errors
      });
  }, [auth.isAuthenticated]);

  const startTour = useCallback(() => {
    setIsTourRunning(true);
  }, []);

  const stopTour = useCallback(() => {
    setIsTourRunning(false);
    // Persist the flag server-side so the tour never auto-starts again
    apiFetch("/api/protected/user/walkthrough", { method: "PUT" }).catch(() => {
      // Best-effort — the tour won't restart unless the user explicitly triggers it
    });
  }, []);

  return (
    <WalkthroughContext.Provider value={{ isTourRunning, startTour, stopTour }}>
      {children}
    </WalkthroughContext.Provider>
  );
};

export const useWalkthrough = (): WalkthroughContextType => {
  const context = useContext(WalkthroughContext);
  if (!context) {
    throw new Error("useWalkthrough must be used within a WalkthroughProvider");
  }
  return context;
};
